import { useCallback } from 'react';

import { type Workspace } from '@air/api/types';

import { useAnalyticsUtilities } from '../hooks/useAnalyticsUtilities';
import { type AuthInfo } from '../types';
import { getInitialUTMTraits } from '../utils/getInitialUTMTraits';
import { trackGTMEvent } from '../utils/trackGTMEvent';

export type ActivatedAccount = {
  creationSource: 'main' | 'workspace-invite' | 'share-link' | 'canva' | null;
  workspaceId?: Workspace['id'];
  workspaceName?: Workspace['name'];
} & AuthInfo;

const ALIAS_IDENTIFY_DELAY_MS = 1000;

export const useTrackActivatedAccount = () => {
  const { track, alias, identify } = useAnalyticsUtilities();

  const trackActivatedAccount = useCallback(
    (params: ActivatedAccount) => {
      const { id, email, firstName, lastName } = params;

      // Creates new alias in Mixpanel to current distinct ID
      alias(id);

      // there can be race conditions in Mixpanel with calling alias and identify too close to each other
      setTimeout(() => {
        identify(id, {
          ...getInitialUTMTraits(),
          email: email,
          first_name: firstName,
          last_name: lastName,
        });
      }, ALIAS_IDENTIFY_DELAY_MS);

      // TODO: this might need an Intercom('update') call

      track(`Activated account`, params, { integrations: { 'Customer.io (Actions)': true, Intercom: true } });

      trackGTMEvent({ event: 'Free Account Sign Up', eventProps: { category: 'self serve', label: 'free', value: 0 } });
    },
    [alias, track, identify],
  );

  return { trackActivatedAccount };
};
